import {
  AddAttendanceEntryForGroupRelation,
  AttendanceSessionEnum,
  GroupAttendanceAggregateCellResponse,
  GroupAttendanceAggregateRowResponse,
  JustificationSave,
} from '@schooly/api';
import {
  ATTENDANCE_DATE,
  ATTENDANCE_PERIOD_ID,
  ATTENDANCE_SESSION,
  Titles,
} from '@schooly/constants';

import {
  cellEntries,
  pickEntryForPoint,
} from '../../Attendance/AttendanceTable/common/trackingPoints';
import { AttendanceGridTrackingPoint } from '../../Attendance/AttendanceTable/common/types';
import { CodeChangeEntry } from '../../Attendance/useJustificationCodeChange';

/** The session or period currently being marked in the modal. */
export type ActiveTrackingPoint = {
  key: string;
  session: AttendanceSessionEnum | null;
  period_id: string | null;
  label: string;
};

/** One student's editable status for a single tracking point. */
export type TakeAttendanceEntry = {
  relation_id: string;
  point_key: string;
  session: AttendanceSessionEnum | null;
  period_id: string | null;
  attendance_code_id: string | null;
  text: string | null;
  attendance_entry_id?: string;
  creator_relation_id?: string | null;
  creator_title?: Titles | null;
  creator_given_name?: string | null;
  creator_last_name?: string | null;
  creator_known_as?: string | null;
};

/** A student's entries keyed by tracking-point key (am / pm / period id). */
export type EntriesByPoint = Record<string, TakeAttendanceEntry | undefined>;

/** A cell belongs to one point: its period if set, otherwise its single session. */
export const cellPointKey = (cell: GroupAttendanceAggregateCellResponse): string | null =>
  cell.meta.period_id ?? cell.meta.sessions[0] ?? null;

export const resolveActivePoint = (
  target: { session: string | null; periodId: string | null },
  points: AttendanceGridTrackingPoint[],
): ActiveTrackingPoint | null => {
  // A period id identifies a point on its own, coupled to a session or not.
  const point = target.periodId
    ? points.find((p) => p.period?.id === target.periodId)
    : points.find((p) => !p.period && p.session === target.session);
  if (!point) return null;

  return {
    key: point.key,
    session: point.session as AttendanceSessionEnum | null,
    period_id: point.period?.id ?? null,
    label: point.label,
  };
};

export const buildRowEntries = (
  row: GroupAttendanceAggregateRowResponse,
  points: AttendanceGridTrackingPoint[],
): EntriesByPoint => {
  const cellByPointKey = new Map(row.cells.map((cell) => [cellPointKey(cell), cell] as const));

  const entries: EntriesByPoint = {};

  for (const point of points) {
    const cell = cellByPointKey.get(point.key);
    // No cell for this point means the student is not expected there.
    if (!cell) continue;

    const matched = pickEntryForPoint(cellEntries(cell.value), point);
    const comment = matched?.comments?.[0];
    const creator = comment?.creator;

    entries[point.key] = {
      relation_id: row.meta.relation_id,
      point_key: point.key,
      session: point.session as AttendanceSessionEnum | null,
      period_id: point.period?.id ?? null,
      attendance_code_id: matched?.attendance_code_id ?? null,
      text: comment?.text ?? null,
      attendance_entry_id: matched?.attendance_entry_id,
      creator_relation_id: creator?.relation_id ?? null,
      creator_title: (creator?.title as Titles | null) ?? null,
      creator_given_name: creator?.given_name ?? null,
      creator_last_name: creator?.last_name ?? null,
      creator_known_as: creator?.known_as ?? null,
    };
  }

  return entries;
};

/**
 * The sessions a mark on this point changes. A justification hangs off AM/PM,
 * so a period coupled to a session targets that session, while a pure period
 * targets none and therefore never moves a justification.
 */
export const pickTargetSessions = (point: {
  session: AttendanceSessionEnum | null;
  period_id: string | null;
}): AttendanceSessionEnum[] => (point.session ? [point.session] : []);

/**
 * Notifications and justifications are session-bound, so a mark on a pure
 * period triggers neither and must not be flagged with the bell.
 */
export const pointSendsNotifications = (
  point: { session: AttendanceSessionEnum | null } | null,
): boolean => !!point?.session;

/** Justifications hang off AM/PM sessions only, so entries without one stay out. */
export const pickSessionEntries = (entries: EntriesByPoint): CodeChangeEntry[] => {
  const sessionEntries: CodeChangeEntry[] = [];

  for (const entry of Object.values(entries)) {
    if (!entry || !entry.session) continue;

    sessionEntries.push({
      session: entry.session,
      attendance_code_id: entry.attendance_code_id,
    });
  }

  return sessionEntries;
};

/** A row as far as saving is concerned: its entries plus any pending justification note. */
export type SaveRow = {
  entries: EntriesByPoint;
  justificationNote?: JustificationSave | null;
};

export const buildSaveRelations = (
  rows: SaveRow[],
  activeKey: string,
): AddAttendanceEntryForGroupRelation[] => {
  const relations: AddAttendanceEntryForGroupRelation[] = [];

  for (const row of rows) {
    const entry = row.entries[activeKey];
    if (!entry?.attendance_code_id) continue;

    relations.push({
      relation_id: entry.relation_id,
      attendance_code_id: entry.attendance_code_id,
      text: entry.text ?? null,
      ...(row.justificationNote && { justification: row.justificationNote }),
    });
  }

  return relations;
};

export const isAllMarkedForPoint = (rows: EntriesByPoint[], activeKey: string): boolean => {
  const entries = rows
    .map((row) => row[activeKey])
    .filter((entry): entry is TakeAttendanceEntry => !!entry);

  return entries.length > 0 && entries.every((entry) => !!entry.attendance_code_id);
};

/**
 * Which tracking point the take-attendance modal is opened on. A plain session
 * sets `session`, a plain period sets `periodId`, and a Registration + Lesson
 * slot coupled to a session sets both.
 */
export type TakeAttendanceTarget = {
  session: string | null;
  periodId: string | null;
};

export const readTakeAttendanceTarget = (params: URLSearchParams): TakeAttendanceTarget => ({
  session: params.get(ATTENDANCE_SESSION),
  periodId: params.get(ATTENDANCE_PERIOD_ID),
});

export const takeAttendancePath = ({
  groupId,
  date,
  session,
  periodId,
}: TakeAttendanceTarget & { groupId: string; date: string }): string => {
  const params = new URLSearchParams({ [ATTENDANCE_DATE]: date });
  if (session) params.set(ATTENDANCE_SESSION, session);
  if (periodId) params.set(ATTENDANCE_PERIOD_ID, periodId);

  return `/groups/${groupId}/attendance?${params.toString()}`;
};

export const takeAttendancePathForPoint = (
  groupId: string,
  date: string,
  point: Pick<AttendanceGridTrackingPoint, 'session' | 'period'>,
): string =>
  takeAttendancePath({
    groupId,
    date,
    session: point.session,
    periodId: point.period?.id ?? null,
  });
